/**
 * Block Catalog
 * 
 * Central registry of all available reusable blocks.
 * Each block is a self-describing module with its component, schemas, and metadata.
 * 
 * To add a new block:
 * 1. Create a new directory under src/blocks/
 * 2. Export a BlockDefinition from its index.ts
 * 3. Import and register it here
 */

import type { BlockCatalog } from './types';
import { iframeEmbedBlock } from './blocks/iframe-embed';
import { multihubLeadFormBlock } from './blocks/multihub-lead-form';
import { virtualTourBlock } from './blocks/virtual-tour';
import { floorplansBlock } from './blocks/floorplans';
import { eliseChatbotBlock } from './blocks/elise-chatbot';
import { eliseScheduleTourIframeBlock } from './blocks/elise-schedule-tour-iframe';
import { googleMapsPlacesBlock } from './blocks/google-maps-places';
import { multihubLeadCaptureBlock } from './blocks/multihub-lead-capture';
import { residentboostChatbotBlock } from './blocks/residentboost-chatbot';
import { rentgrataWidgetBlock } from './blocks/rentgrata-widget';
import { rentcafeChatbotBlock } from './blocks/rentcafe-chatbot';
import { zumaChatbotBlock } from './blocks/zuma-chatbot';
import { ga4TrackingBlock } from './blocks/ga4-tracking';
import { googleAdsConversionsBlock } from './blocks/google-ads-conversions';
import { rentcafeIntegrationBlock } from './blocks/rentcafe-integration';
import { callrailTrackingBlock } from './blocks/callrail-tracking';
import { googleTagManagerBlock } from './blocks/google-tag-manager';
import { microsoftClarityBlock } from './blocks/microsoft-clarity';
import { facebookPixelBlock } from './blocks/facebook-pixel';
import { openAiPixelBlock } from './blocks/openai-pixel';
import { imageTrackingPixelBlock } from './blocks/image-tracking-pixel';
import { basisTrackingPixelBlock } from './blocks/basis-tracking-pixel';
import { flexPaymentBlock } from './blocks/flex-payment';
import { hylyAttributionBlock } from './blocks/hyly-attribution';
import { jsonLdSchemaBlock } from './blocks/json-ld-schema';
import { apartmentSyncBlock } from './blocks/apartment-sync';
import { apartmentSyncLoaderBlock } from './blocks/apartment-sync-loader';
import { multihubTourSchedulerBlock } from './blocks/multihub-tour-scheduler';
import { apartmentSyncMapBlock } from './blocks/apartment-sync-map';
import { engrainSitemapBlock } from './blocks/engrain-sitemap';
import { realpageOllBlock } from './blocks/realpage-oll';
import { realpageCalendarBlock } from './blocks/realpage-calendar';
import { knockChatbotBlock } from './blocks/knock-chatbot';
import { instagramOembedBlock } from './blocks/instagram-oembed';
import { audioeyeAccessibilityBlock } from './blocks/audioeye-accessibility';
import { hyperlocalLinksBlock } from './blocks/hyperlocal-links';
import { audioeyeWidgetBlock } from './blocks/audioeye-widget';
import { cortLeadFormBlock } from './blocks/cort-lead-form';
import { adtaxiTrackingBlock } from './blocks/adtaxi-tracking';
import { entrataChatbotBlock } from './blocks/entrata-chatbot';
import { anyonehomeChatbotBlock } from './blocks/anyonehome-chatbot';
import { betterbotChatbotBlock } from './blocks/betterbot-chatbot';
import { privacyConsentBlock } from './blocks/privacy-consent';
import { leaChatbotBlock } from './blocks/lea-chatbot';
import { perqFloorPlanBlock } from './blocks/perq-floor-plan';

/**
 * The complete block catalog.
 * Blocks are keyed by their unique ID.
 */
export const blockCatalog: BlockCatalog = {
  'iframe-embed': iframeEmbedBlock,
  'multihub-lead-form': multihubLeadFormBlock,
  'virtual-tour': virtualTourBlock,
  'floorplans': floorplansBlock,
  'elise-chatbot': eliseChatbotBlock,
  'elise-schedule-tour-iframe': eliseScheduleTourIframeBlock,
  'google-maps-places': googleMapsPlacesBlock,
  'multihub-lead-capture': multihubLeadCaptureBlock,
  'residentboost-chatbot': residentboostChatbotBlock,
  'rentgrata-widget': rentgrataWidgetBlock,
  'rentcafe-chatbot': rentcafeChatbotBlock,
  'zuma-chatbot': zumaChatbotBlock,
  'ga4-tracking': ga4TrackingBlock,
  'google-ads-conversions': googleAdsConversionsBlock,
  'rentcafe-integration': rentcafeIntegrationBlock,
  'callrail-tracking': callrailTrackingBlock,
  'google-tag-manager': googleTagManagerBlock,
  'microsoft-clarity': microsoftClarityBlock,
  'facebook-pixel': facebookPixelBlock,
  'openai-pixel': openAiPixelBlock,
  'image-tracking-pixel': imageTrackingPixelBlock,
  'basis-tracking-pixel': basisTrackingPixelBlock,
  'flex-payment': flexPaymentBlock,
  'hyly-attribution': hylyAttributionBlock,
  'json-ld-schema': jsonLdSchemaBlock,
  'apartment-sync': apartmentSyncBlock,
  'apartment-sync-loader': apartmentSyncLoaderBlock,
  'multihub-tour-scheduler': multihubTourSchedulerBlock,
  'apartment-sync-map': apartmentSyncMapBlock,
  'engrain-sitemap': engrainSitemapBlock,
  'realpage-oll': realpageOllBlock,
  'realpage-calendar': realpageCalendarBlock,
  'knock-chatbot': knockChatbotBlock,
  'instagram-oembed': instagramOembedBlock,
  'audioeye-accessibility': audioeyeAccessibilityBlock,
  'hyperlocal-links': hyperlocalLinksBlock,
  'audioeye-widget': audioeyeWidgetBlock,
  'cort-lead-form': cortLeadFormBlock,
  'adtaxi-tracking': adtaxiTrackingBlock,
  'entrata-chatbot': entrataChatbotBlock,
  'anyonehome-chatbot': anyonehomeChatbotBlock,
  'betterbot-chatbot': betterbotChatbotBlock,
  'privacy-consent': privacyConsentBlock,
  'lea-chatbot': leaChatbotBlock,
  'perq-floor-plan': perqFloorPlanBlock
};

/**
 * Get a block definition by ID.
 */
export function getBlock(blockId: string) {
  console.log('getBlock', blockId);
  console.log('blockCatalog', blockCatalog);
  return blockCatalog[blockId] ?? null;
}

/**
 * Get all blocks in a specific category.
 */
export function getBlocksByCategory(category: string) {
  return Object.values(blockCatalog).filter(b => b.category === category);
}

/**
 * Get all unique categories from the catalog.
 */
export function getCategories(): string[] {
  const categories = new Set(Object.values(blockCatalog).map(b => b.category));
  return Array.from(categories).sort();
}

/**
 * Search blocks by name, description, or tags.
 */
export function searchBlocks(query: string) {
  const lower = query.toLowerCase();
  return Object.values(blockCatalog).filter(block =>
    block.name.toLowerCase().includes(lower) ||
    block.description.toLowerCase().includes(lower) ||
    block.tags?.some(tag => tag.toLowerCase().includes(lower))
  );
}
